import { useCallback, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import {
  $getRoot,
  $insertNodes,
  EditorState,
  LexicalEditor,
  TextNode,
} from 'lexical';
import { MentionNode } from './mention-node';
import MentionPlugin from './mention-plugin';
import WordCount from './word-count';
import './styles.scss';
import RichTextEditorToolbar from './rich-text-editor-toolbar';
import { ExtendedTextNode } from './extended-text-node-plugin';

// Taken from the documentation: https://lexical.dev/docs/react/plugins#lexicalautolinkplugin
const URL_REGEX =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/;

const AUTOLINK_MATCHERS = [
  (text: string) => {
    const match = URL_REGEX.exec(text);
    if (match === null) return null;
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    };
  },
];

interface LexicalBodyEditorProps {
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (html: string) => void;
}

function LoadInitialContent({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !html) return;
    loaded.current = true;
    editor.update(() => {
      const preprocessed = html;
      const parser = new DOMParser();
      const dom = parser.parseFromString(preprocessed, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });
  }, [editor, html]);

  return null;
}

// This repo has more examples: https://github.com/facebook/lexical/blob/main/examples/react-rich/src/App.tsx
export default function LexicalBodyEditor({
  defaultValue = '',
  placeholder = 'What would you like to share?',
  disabled = false,
  onChange,
}: LexicalBodyEditorProps) {
  const initialConfig = {
    namespace: 'LexicalBodyEditor',
    theme: {
      root: 'lexical-rich-text-editor__content',
      paragraph: 'lexical-rich-text-editor__paragraph',
      heading: {
        h1: 'lexical-rich-text-editor__h1',
        h2: 'lexical-rich-text-editor__h2',
        h3: 'lexical-rich-text-editor__h3',
      },
      list: {
        ul: 'lexical-rich-text-editor__ul',
        ol: 'lexical-rich-text-editor__ol',
        listitem: 'lexical-rich-text-editor__li',
      },
      link: 'lexical-rich-text-editor__link',
      text: {
        bold: 'lexical-rich-text-editor__bold',
        italic: 'lexical-rich-text-editor__italic',
        underline: 'lexical-rich-text-editor__underline',
        strikethrough: 'lexical-rich-text-editor__strikethrough',
        code: 'lexical-rich-text-editor__code',
      },
    },
    nodes: [
      // ExtendedTextNode comes from the documentation https://lexical.dev/docs/concepts/serialization#handling-extended-html-styling
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
        withKlass: ExtendedTextNode,
      },
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      MentionNode,
    ],
    editable: !disabled,
    onError: (error: Error) => {
      console.error('LexicalBodyEditor error:', error);
    },
  };

  const handleChange = useCallback(
    (_editorState: EditorState, editor: LexicalEditor) => {
      editor.read(() => {
        const rawHtml = $generateHtmlFromNodes(editor);
        onChange?.(rawHtml);
      });
    },
    [onChange]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="lexical-rich-text-editor__wrapper">
        <RichTextPlugin
          contentEditable={
            <ContentEditable className="lexical-rich-text-editor__input" />
          }
          placeholder={
            <div className="lexical-rich-text-editor__placeholder">
              {placeholder}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <div className="lexical-rich-text-editor__footer">
          <RichTextEditorToolbar />
          <WordCount />
        </div>
      </div>
      <OnChangePlugin onChange={handleChange} />
      <HistoryPlugin />
      <LinkPlugin />
      <ListPlugin />
      <AutoLinkPlugin matchers={AUTOLINK_MATCHERS} />

      <MentionPlugin />

      {defaultValue && <LoadInitialContent html={defaultValue} />}
    </LexicalComposer>
  );
}
